import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { formatChatDate, groupChatsByDate } from '../../utils/date';
import './AccountModal.css';

const formatChatTitle = (chat) => {
  if (chat.title) return chat.title;
  if (chat.firstMessage) return chat.firstMessage;
  return 'New Chat';
};

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const CONTEXT_CHARS = 25;

/** Plain text with match styled (black in light mode, white in dark) - no background */
const HighlightedText = ({ text, searchTerm, className = '', darkMode }) => {
  if (!text) return null;
  if (!searchTerm || !searchTerm.trim()) {
    return <span className={className}>{text}</span>;
  }
  const term = searchTerm.trim();
  const escaped = escapeRegExp(term);
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  const matchColor = darkMode ? 'text-white' : 'text-black';
  return (
    <span className={className}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className={`font-semibold ${matchColor}`}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

/** Snippet with context around match - shows chars on left/right so user sees where match is */
const SearchSnippet = ({ text, searchTerm, className = '', darkMode }) => {
  if (!text) return null;
  if (!searchTerm || !searchTerm.trim()) {
    return <span className={className}>{text}</span>;
  }
  const term = searchTerm.trim();
  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerTerm);

  if (matchIndex === -1) {
    return <span className={className}>{text}</span>;
  }

  const matchEnd = matchIndex + term.length;
  const start = Math.max(0, matchIndex - CONTEXT_CHARS);
  const end = Math.min(text.length, matchEnd + CONTEXT_CHARS);

  const before = text.slice(start, matchIndex);
  const match = text.slice(matchIndex, matchEnd);
  const after = text.slice(matchEnd, end);

  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';

  const matchColor = darkMode ? 'text-white' : 'text-black';

  return (
    <span className={className}>
      {prefix}{before}
      <span className={`font-semibold ${matchColor}`}>{match}</span>
      {after}{suffix}
    </span>
  );
};

export default function SearchModal({ isOpen, onClose, onChatSelect, onNewChat, darkMode }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatList, setChatList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const fetchChatList = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setChatList([]);
        setIsLoadingChats(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat?page=1&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const chats = (response.data.chats || []).filter(chat => !chat.projectId);
      setChatList(chats);
    } catch (error) {
      console.error('Error fetching chat list:', error);
      setChatList([]);
    } finally {
      setIsLoadingChats(false);
    }
  }, []);

  const searchChats = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/search?q=${encodeURIComponent(query)}&includeMessages=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const results = (response.data.chats || []).filter(chat => !chat.projectId);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching chats:', error);
      setSearchError('Failed to search chats');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((query) => searchChats(query), 300),
    [searchChats]
  );

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    } else {
      setSearchResults([]);
      setIsSearching(false);
      setSearchError(null);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setIsLoadingChats(true);
      fetchChatList();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, fetchChatList]);

  const handleChatClick = (chat) => {
    onChatSelect?.(chat._id);
    navigate(`/chat/${chat._id}${chat.clientId ? `?client=${chat.clientId}` : ''}`);
    onClose();
  };

  const handleNewChat = () => {
    onNewChat?.();
    onClose();
  };

  if (!isOpen) return null;

  const displayChats = searchTerm.trim() ? searchResults : chatList;
  const showLoading = searchTerm.trim() ? isSearching : isLoadingChats;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="account-modal account-modal-shape relative w-full max-w-xl h-[70vh] flex flex-col bg-white dark:bg-[#1a1a1a] shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: search input + close */}
        <div className="flex-shrink-0 flex items-center gap-2 p-4 border-b border-gray-200 dark:border-[#2a2a2a]">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 dark:text-gray-500" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search chats..."
              className="w-full pl-10 pr-4 py-1 bg-transparent border-0 text-base focus:outline-none focus:ring-0 text-gray-900 dark:text-white placeholder:text-gray-500"
              style={{ fontFamily: 'var(--chat-font-family)' }}
            />
          </div>
          <button
            onClick={onClose}
            className="account-modal-btn p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* New chat button - commented out */}
        {/* <button
          onClick={handleNewChat}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-[#222] transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" fill="currentColor">
            <path fillRule="evenodd" d="M6.169 6.331a3 3 0 0 0-.833 1.6l-.338 1.912a1 1 0 0 0 1.159 1.159l1.912-.338a3 3 0 0 0 1.6-.833l3.07-3.07l2-2A.9.9 0 0 0 15 4.13A3.13 3.13 0 0 0 11.87 1a.9.9 0 0 0-.632.262l-2 2zm3.936-1.814L7.229 7.392a1.5 1.5 0 0 0-.416.8L6.6 9.4l1.208-.213l.057-.01a1.5 1.5 0 0 0 .743-.406l2.875-2.876a1.63 1.63 0 0 0-1.378-1.378m2.558.199a3.14 3.14 0 0 0-1.379-1.38l.82-.82a1.63 1.63 0 0 1 1.38 1.38zM8 2.25a.75.75 0 0 0-.75-.75H4.5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h7a3 3 0 0 0 3-3V8.75a.75.75 0 0 0-1.5 0v2.75a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7A1.5 1.5 0 0 1 4.5 3h2.75A.75.75 0 0 0 8 2.25" clipRule="evenodd"/>
          </svg>
          <span className="text-sm font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'var(--chat-font-family)' }}>
            New chat
          </span>
        </button> */}

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto pt-6 pb-2">
          {searchError && (
            <div className="px-4 py-3 text-sm text-red-500 dark:text-red-400">
              {searchError}
            </div>
          )}

          {showLoading && (
            <div className="px-4 py-2">
              <SkeletonTheme baseColor={darkMode ? '#252525' : '#e6e6e6'} highlightColor={darkMode ? '#333' : '#d8d8d8'}>
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <Skeleton circle width={40} height={40} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Skeleton height={14} width="60%" className="mb-2" />
                      <Skeleton height={12} width="100%" />
                    </div>
                  </div>
                ))}
              </SkeletonTheme>
            </div>
          )}

          {!showLoading && searchTerm && searchResults.length === 0 && !searchError && (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
              No chats found
            </div>
          )}

          {!showLoading && displayChats.length > 0 && (
            <div className="px-2">
              {groupChatsByDate(displayChats).map(([dateLabel, dateChats], index) => (
                <div key={dateLabel} className={`mb-2 ${index > 0 ? 'pt-3' : ''}`}>
                  <h3 className="px-4 py-1 text-xs font-medium text-gray-500 dark:text-gray-400" style={{ fontFamily: 'var(--chat-font-family)' }}>
                    {dateLabel}
                  </h3>
                  {dateChats.map((chat) => (
                    <button
                      key={chat._id}
                      onClick={() => handleChatClick(chat)}
                      className="account-modal-item account-modal-item-lg w-full flex items-start gap-3 px-4 py-3 text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
                            <HighlightedText text={formatChatTitle(chat)} searchTerm={searchTerm} darkMode={darkMode} />
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                            {formatChatDate(chat.lastMessageAt || chat.createdAt)}
                          </span>
                        </div>
                        {chat.firstMessage && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            <SearchSnippet text={chat.firstMessage} searchTerm={searchTerm} darkMode={darkMode} />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          {!showLoading && !searchTerm && chatList.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <p className="text-[13px] text-gray-400 dark:text-[#555]">
                Your conversations will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
